import React, { useState, useEffect } from 'react';
import { startSession, getActiveSession } from '../services/FishingService';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (message: string, type?: 'info' | 'success' | 'error') => void;
}

export const SessionStartModal: React.FC<Props> = ({ isOpen, onClose, onSuccess }) => {
  const [waterTemp, setWaterTemp] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasActiveSession, setHasActiveSession] = useState(false);

  useEffect(() => {
    if (isOpen) {
      checkActiveSession();
    }
  }, [isOpen]);

  const checkActiveSession = async () => {
    const active = await getActiveSession();
    setHasActiveSession(!!active);
  };

  if (!isOpen) return null;

  const handleStart = async () => {
    setIsLoading(true);
    try {
      const temp = waterTemp ? parseFloat(waterTemp) : undefined;
      
      await startSession({
        waterTemp: temp && !isNaN(temp) ? temp : undefined,
        notes: notes || undefined
      });

      onSuccess('🎣 Fiskepass startat!', 'success');
      onClose();
      
      // Reset
      setWaterTemp('');
      setNotes('');
    } catch (error) {
      console.error('Error starting session:', error);
      onSuccess('❌ Kunde inte starta fiskepass', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <div style={styles.header}>
          <h2>🎣 Starta Fiskepass</h2>
          <button onClick={onClose} style={styles.closeButton}>✕</button>
        </div>

        <div style={styles.content}>
          <p style={styles.description}>
            Starta ett fiskepass för att gruppera fångster och samla statistik.
          </p>

          {hasActiveSession && (
            <div style={{
              padding: '12px 16px',
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '8px',
              marginBottom: '16px',
              color: 'rgb(248, 113, 113)',
              fontSize: '14px'
            }}>
              ⚠️ <strong>OBS!</strong> Du har redan ett aktivt pass som kommer att avslutas automatiskt.
            </div>
          )}

          <div style={styles.field}>
            <label style={styles.label}>Vattentemperatur (°C)</label>
            <input
              type="number"
              step="0.5"
              value={waterTemp}
              onChange={e => setWaterTemp(e.target.value)}
              placeholder="T.ex. 12.5"
              style={styles.input}
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Anteckningar (frivilligt)</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="T.ex. Soligt, vindstilla..."
              style={styles.textarea}
              rows={3}
            />
          </div>

          <div style={styles.actions}>
            <button
              onClick={onClose}
              style={styles.cancelButton}
              disabled={isLoading}
            >
              Avbryt
            </button>
            <button
              onClick={handleStart}
              style={styles.startButton}
              disabled={isLoading}
            >
              {isLoading ? 'Startar...' : 'Starta Pass'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '12px',
    maxWidth: '400px',
    width: '90%',
    maxHeight: '80vh',
    overflow: 'auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    borderBottom: '1px solid #e0e0e0',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#666',
  },
  content: {
    padding: '20px',
  },
  description: {
    color: '#666',
    marginBottom: '20px',
    fontSize: '14px',
  },
  field: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    fontWeight: '500',
    marginBottom: '8px',
    color: '#333',
  },
  input: {
    width: '100%',
    padding: '12px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '16px',
    boxSizing: 'border-box' as const,
  },
  textarea: {
    width: '100%',
    padding: '12px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '16px',
    boxSizing: 'border-box' as const,
    resize: 'vertical' as const,
  },
  actions: {
    display: 'flex',
    gap: '12px',
    marginTop: '24px',
  },
  cancelButton: {
    flex: 1,
    padding: '16px',
    border: '2px solid #e0e0e0',
    backgroundColor: 'white',
    color: '#666',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '500',
  },
  startButton: {
    flex: 2,
    padding: '16px',
    border: '2px solid #007AFF',
    backgroundColor: '#007AFF',
    color: 'white',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '500',
  },
};

export default SessionStartModal;
